import { Mark } from "@tiptap/core";

/**
 * TipTap mark that wraps selected text in a <span class="tc-{color}">
 * to apply text-color classes defined in common/sections/richtext.css.
 *
 * Colors resolve through tenant CSS variables so they track brand changes.
 */

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textColor: {
      setTextColor: (color: string) => ReturnType;
      unsetTextColor: () => ReturnType;
    };
  }
}

const VALID_COLORS = ["primary", "secondary", "text", "muted", "light", "dark"];

export const TextColorMark = Mark.create({
  name: "textColor",

  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const classes = element.className?.split(" ") || [];
          const colorClass = classes.find((c) =>
            /^tc-(primary|secondary|text|muted|light|dark)$/.test(c),
          );
          return colorClass ? colorClass.replace("tc-", "") : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.color || !VALID_COLORS.includes(attributes.color)) {
            return {};
          }
          return { class: `tc-${attributes.color}` };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[class]", getAttrs: (el) => {
      const element = el as HTMLElement;
      const hasColor = VALID_COLORS.some((c) => element.classList.contains(`tc-${c}`));
      return hasColor ? {} : false;
    }}];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setTextColor:
        (color: string) =>
        ({ commands }) => {
          if (!VALID_COLORS.includes(color)) return false;
          return commands.setMark(this.name, { color });
        },
      unsetTextColor:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
